import React, { useState } from "react";
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  format,
  addDays,
  isSameMonth,
  addMonths,
  subMonths,
  isSameDay,
  startOfDay,
} from "date-fns";
import "./styles.css";
import { Project, ProjectStatus } from "../../../types/project";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useUILabel } from "../../../hooks/useUILabel";
import {
  Box,
  Typography,
  IconButton,
  styled,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Divider,
  Stack,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ClockIcon from "@mui/icons-material/Timer";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";

const StyledHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2.5, 3),
  borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
  backgroundColor: theme.palette.mode === "dark"
    ? "rgba(255, 255, 255, 0.03)"
    : "rgba(0, 0, 0, 0.01)",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5, 1.5),
  },
}));

const StyledNavButton = styled(IconButton)(({ theme }) => ({
  borderRadius: 10,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    backgroundColor: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.1)"
      : "rgba(0, 0, 0, 0.05)",
    transform: "scale(1.1)",
  },
}));

const StyledMonthTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.25rem",
  fontWeight: 700,
  color: theme.palette.text.primary,
  letterSpacing: "0.5px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "1rem",
  },
}));

const StyledDaysHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  padding: theme.spacing(1.5, 0),
  borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
  backgroundColor: theme.palette.mode === "dark"
    ? "rgba(255, 255, 255, 0.02)"
    : "rgba(0, 0, 0, 0.01)",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.75, 0),
  },
}));

const StyledDayHeader = styled(Box)(({ theme }) => ({
  flex: 1,
  textAlign: "center",
  fontSize: "0.875rem",
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  padding: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.65rem",
    padding: theme.spacing(0.5),
    letterSpacing: "0.2px",
  },
}));

const StyledCalendarBody = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  minHeight: "60vh",
}));

const StyledCalendarRow = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)"}`,
  "&:last-child": {
    borderBottom: "none",
  },
}));

const StyledCalendarCell = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isToday" && prop !== "isDisabled",
})<{ isToday?: boolean; isDisabled?: boolean; }>(
  ({ theme, isToday, isDisabled }) => ({
    flex: 1,
    minHeight: "120px",
    padding: theme.spacing(1),
    borderRight: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)"}`,
    position: "relative",
    cursor: "default",
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    backgroundColor: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.01)"
      : "rgba(0, 0, 0, 0.005)",
    overflow: "hidden",
    boxSizing: "border-box",
    [theme.breakpoints.down("sm")]: {
      minHeight: "80px",
      padding: theme.spacing(0.5),
    },
    "&:last-child": {
      borderRight: "none",
    },
    "&:hover": {
      backgroundColor: theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.05)"
        : "rgba(0, 0, 0, 0.02)",
    },
    ...(isToday && {
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      backgroundColor: theme.palette.mode === "dark"
        ? "rgba(25, 118, 210, 0.1)"
        : "rgba(25, 118, 210, 0.05)",
      [theme.breakpoints.down("sm")]: {
        borderLeft: `2px solid ${theme.palette.primary.main}`,
      },
    }),
    ...(isDisabled && {
      color: theme.palette.text.disabled,
      pointerEvents: "none",
      opacity: 0.4,
    }),
  })
);

const StyledDayNumber = styled(Typography)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(1),
  right: theme.spacing(1),
  fontSize: "0.875rem",
  fontWeight: 600,
  color: theme.palette.text.primary,
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.7rem",
    top: theme.spacing(0.5),
    right: theme.spacing(0.5),
  },
}));

const StyledJobCard = styled(Box)<{ bgColor: string; isSelected?: boolean; }>(
  ({ theme, bgColor, isSelected }) => ({
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    cursor: "pointer",
    padding: theme.spacing(0.75, 1),
    margin: theme.spacing(0.5, 0),
    borderRadius: 8,
    backgroundColor: bgColor,
    color: "white",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: theme.palette.mode === "dark"
      ? "0 2px 8px rgba(0, 0, 0, 0.3)"
      : "0 2px 8px rgba(0, 0, 0, 0.15)",
    overflow: "hidden",
    boxSizing: "border-box",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0.4, 0.6),
      margin: theme.spacing(0.2, 0),
      borderRadius: 4,
    },
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.palette.mode === "dark"
        ? "0 4px 12px rgba(0, 0, 0, 0.4)"
        : "0 4px 12px rgba(0, 0, 0, 0.2)",
    },
    ...(isSelected && {
      boxShadow: `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${bgColor}80`,
      [theme.breakpoints.down("sm")]: {
        boxShadow: `0 0 0 1px ${theme.palette.background.paper}, 0 0 0 2px ${bgColor}80`,
      },
    }),
  })
);

const StyledJobTitle = styled(Typography)(({ theme }) => ({
  fontSize: "0.8rem",
  fontWeight: 600,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  marginTop: 4,
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  display: "block",
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.6rem",
    marginTop: 2,
    lineHeight: 1.2,
  },
}));

const StyledJobTime = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 4,
  fontSize: "0.75rem",
  fontWeight: 500,
  marginBottom: 4,
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.55rem",
    gap: 2,
    marginBottom: 2,
  },
}));

const StyledDetailLink = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 4,
  marginTop: theme.spacing(1),
  fontSize: "0.8rem",
  fontWeight: 700,
  cursor: "pointer",
  color: "white",
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.6rem",
    gap: 2,
    marginTop: theme.spacing(0.5),
  },
  "&:hover": {
    gap: 8,
    "& .arrow-icon": {
      transform: "translateX(4px)",
    },
  },
  "& .arrow-icon": {
    transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  },
}));

const MonthlyCalendar = ({ data }: { data: Project[] | undefined; }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const locationLabel = useUILabel("common.location");
  const dateFormat = "MMMM yyyy";
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedJob, setSelectedJob] = useState(-1);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const touchStartTime = React.useRef<number>(0);
  const touchStartTarget = React.useRef<EventTarget | null>(null);

  const changeMonthHandle = (btnType: "prev" | "next") => {
    if (btnType === "prev") {
      setCurrentMonth(subMonths(currentMonth, 1));
    }
    if (btnType === "next") {
      setCurrentMonth(addMonths(currentMonth, 1));
    }
  };

  //   const onDateClickHandle = (day: any) => {
  //     setSelectedDate(day);
  //     // showDetailsHandle(dayStr);
  //   };

  const getTodaysjobs = (todaysDate: Date): Project[] => {
    const list = data?.filter((project: Project) => {
      const startDate = dayjs(project.start_date);
      const projectDate = startDate.get("date");
      const projectYear = startDate.get("year");
      const projectMonth = startDate.get("month");
      return (
        (project.status === ProjectStatus.SCHEDULED ||
          project.status === ProjectStatus.IN_PROGRESS ||
          project.status === ProjectStatus.PUBLISHED) &&
        todaysDate.getDate() == projectDate &&
        todaysDate.getMonth() == projectMonth &&
        todaysDate.getFullYear() == projectYear
      );
    });
    return list || [];
  };

  function renderHeader() {
    return (
      <StyledHeader>
        <StyledNavButton onClick={prevMonth} size="small">
          <ChevronLeftIcon />
        </StyledNavButton>
        <StyledMonthTitle>{format(currentMonth, dateFormat)}</StyledMonthTitle>
        <StyledNavButton onClick={nextMonth} size="small">
          <ChevronRightIcon />
        </StyledNavButton>
      </StyledHeader>
    );
  }

  function renderDays() {
    const dateFormat = "EEEE";
    const days = [];

    let startDate = startOfWeek(currentMonth);

    for (let i = 0; i < 7; i++) {
      days.push(
        <StyledDayHeader key={i}>
          {format(addDays(startDate, i), dateFormat)}
        </StyledDayHeader>
      );
    }

    return <StyledDaysHeader>{days}</StyledDaysHeader>;
  }

  const isDateSelected = (day: Date): boolean => {
    if (!selectedDate) {
      return false;
    }
    // Normalize both dates to midnight local time for accurate comparison using startOfDay
    const dayNormalized = startOfDay(day);
    const selectedNormalized = startOfDay(selectedDate);
    const result = dayNormalized.getTime() === selectedNormalized.getTime();
    return result;
  };

  function renderJobs(day: Date) {
    // Normalize day to local timezone for consistent comparison using startOfDay
    const localDay = startOfDay(day);
    const jobs = getTodaysjobs(localDay);

    // Limit jobs on mobile to prevent layout issues
    const maxJobsMobile = 2;
    const jobsToShow = isMobile ? jobs.slice(0, maxJobsMobile) : jobs;
    const remainingJobs = isMobile ? jobs.length - maxJobsMobile : 0;

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.2,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          maxHeight: { xs: "45px", sm: "none" },
          overflowY: { xs: "auto", sm: "visible" },
          overflowX: "hidden",
          boxSizing: "border-box",
          "&::-webkit-scrollbar": {
            width: "2px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(255, 255, 255, 0.3)",
            borderRadius: "2px",
          },
        }}
      >
        {jobsToShow.map((project) => {
          let bgColor = "#0288D1"; // default color
          if (project.status === ProjectStatus.PUBLISHED) {
            bgColor = "#0288D1";
          } else if (project.status === ProjectStatus.SCHEDULED) {
            bgColor = "#ED6C02";
          } else if (project.status === ProjectStatus.IN_PROGRESS) {
            bgColor = "#FF0000";
          }
          const isSelected = selectedJob === project.id;

          if (isSelected) {
            return (
              <StyledJobCard
                key={project.id}
                bgColor={bgColor as string}
                isSelected={isSelected}
              >
                <StyledJobTime>
                  <ClockIcon sx={{ fontSize: { xs: "0.55rem", sm: "0.875rem" }, flexShrink: 0 }} />
                  <Typography
                    component="span"
                    sx={{
                      fontSize: { xs: "0.55rem", sm: "0.75rem" },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    {dayjs(project.start_date).format("hh:mm A z")}
                  </Typography>
                </StyledJobTime>
                <StyledJobTitle
                  onClick={() => {
                    setSelectedJob(-1);
                  }}
                >
                  {project.title}
                </StyledJobTitle>
                <StyledDetailLink onClick={() => navigate(`/works/${project?.id}`)}>
                  <Typography
                    component="span"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    Go To Detail
                  </Typography>
                  <ArrowForwardIcon
                    className="arrow-icon"
                    sx={{
                      fontSize: { xs: "0.6rem", sm: "0.875rem" },
                      flexShrink: 0,
                    }}
                  />
                </StyledDetailLink>
              </StyledJobCard>
            );
          }
          return (
            <StyledJobCard
              key={project.id}
              bgColor={bgColor as string}
              onClick={(e) => {
                e.stopPropagation();
                if (isMobile) {
                  setSelectedProject(project);
                  setOpenModal(true);
                } else {
                  setSelectedJob(project.id);
                }
              }}
            >
              <StyledJobTitle>{project.title}</StyledJobTitle>
            </StyledJobCard>
          );
        })}
        {remainingJobs > 0 && (
          <Box
            sx={{
              mt: 0.2,
              px: 0.5,
              py: 0.25,
              borderRadius: 1,
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              fontSize: { xs: "0.5rem", sm: "0.65rem" },
              fontWeight: 600,
              textAlign: "center",
              color: "white",
            }}
          >
            +{remainingJobs} more
          </Box>
        )}
      </Box>
    );
  }

  function renderCells() {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const dateFormat = "d";
    const rows = [];

    let days = [];
    let day = startDate;
    let formattedDate = "";

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        formattedDate = format(day, dateFormat);
        // Normalize day to local timezone using startOfDay
        const localDay = startOfDay(day);
        const isToday = isSameDay(localDay, new Date());
        const isDisabled = !isSameMonth(localDay, monthStart);
        const isSelected = isDateSelected(localDay);
        const hasJobs = getTodaysjobs(localDay).length > 0;
        days.push(
          <StyledCalendarCell
            key={day.toString()}
            isToday={isToday}
            isDisabled={isDisabled}
            onTouchStart={(e) => {
              // Store touch start info to prevent double trigger with onClick
              touchStartTime.current = Date.now();
              touchStartTarget.current = e.currentTarget;
            }}
            onTouchEnd={(e) => {
              // Normalize day to local timezone using startOfDay
              const localDay = startOfDay(day);
              // Handle touch events for mobile
              if (isMobile && !isDisabled && hasJobs) {
                e.stopPropagation();
                const isSelected = isDateSelected(localDay);
                if (isSelected) {
                  setSelectedDate(null);
                  setSelectedJob(-1);
                } else {
                  setSelectedDate(localDay);
                }
              }
            }}
            onClick={(e) => {
              // Normalize day to local timezone using startOfDay
              const localDay = startOfDay(day);
              // Only handle click for mobile, and only if it's not a touch event (to prevent double trigger)
              const timeSinceTouch = Date.now() - touchStartTime.current;
              const isTouchEvent = timeSinceTouch < 300 && touchStartTarget.current === e.currentTarget;

              if (isTouchEvent) {
                return;
              }

              e.stopPropagation();
              // Handle click for both mobile and desktop
              if (!isDisabled && hasJobs) {
                const isSelectedLocal = isDateSelected(localDay);
                if (isSelectedLocal) {
                  setSelectedDate(null);
                  setSelectedJob(-1);
                } else {
                  setSelectedDate(localDay);
                }
              }
            }}
            sx={{
              cursor: !isDisabled && hasJobs ? "pointer" : "default",
              userSelect: "none",
              WebkitTapHighlightColor: "transparent",
            }}
          >
            <StyledDayNumber sx={{ pointerEvents: "none" }}>{formattedDate}</StyledDayNumber>
            <Box sx={{ height: { xs: 16, sm: 24 }, pointerEvents: "none" }} />
            <Box
              sx={{
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                overflow: "hidden",
                boxSizing: "border-box",
              }}
            >
              {renderJobs(localDay)}
            </Box>
          </StyledCalendarCell>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <StyledCalendarRow key={day.toString()}>
          {days}
        </StyledCalendarRow>
      );
      days = [];
    }
    return <StyledCalendarBody>{rows}</StyledCalendarBody>;
  }

  function prevMonth() {
    setCurrentMonth(subMonths(currentMonth, 1));
    setSelectedDate(null);
    setSelectedJob(-1);
  }

  function nextMonth() {
    setCurrentMonth(addMonths(currentMonth, 1));
    setSelectedDate(null);
    setSelectedJob(-1);
  }
  //   const renderFooter = () => {
  //     return (
  //       <div className="header row flex-middle">
  //         <div className="col col-start">
  //           <div className="icon" onClick={() => changeWeekHandle("prev")}>
  //             prev week
  //           </div>
  //         </div>
  //         <div>{currentWeek}</div>
  //         <div className="col col-end" onClick={() => changeWeekHandle("next")}>
  //           <div className="icon">next week</div>
  //         </div>
  //       </div>
  //     );
  //   };
  const getStatusColor = (status: ProjectStatus) => {
    switch (status) {
      case ProjectStatus.PUBLISHED:
        return "#0288D1";
      case ProjectStatus.SCHEDULED:
        return "#ED6C02";
      case ProjectStatus.IN_PROGRESS:
        return "#FF0000";
      default:
        return "#757575";
    }
  };

  const getStatusLabel = (status: ProjectStatus) => {
    switch (status) {
      case ProjectStatus.PUBLISHED:
        return "Published";
      case ProjectStatus.SCHEDULED:
        return "Scheduled";
      case ProjectStatus.IN_PROGRESS:
        return "In Progress";
      default:
        return status;
    }
  };

  return (
    <Box>
      {renderHeader()}
      <Box>
        {renderDays()}
        {renderCells()}
      </Box>

      {/* Job Detail Modal - Mobile Only */}
      {isMobile && (
        <Dialog
          open={openModal}
          onClose={() => {
            setOpenModal(false);
            setSelectedProject(null);
          }}
          maxWidth="md"
          fullWidth
          fullScreen
          PaperProps={{
            sx: {
              borderRadius: 0,
              maxHeight: "100%",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pb: 1,
            }}
          >
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              Job Details
            </Typography>
            <IconButton
              onClick={() => {
                setOpenModal(false);
                setSelectedProject(null);
              }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            {selectedProject && (
              <Stack spacing={2.5}>
                {/* Title and Status */}
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      mb: 1.5,
                      fontSize: { xs: "1.25rem", sm: "1.5rem" },
                    }}
                  >
                    {selectedProject.title}
                  </Typography>
                  <Chip
                    label={getStatusLabel(selectedProject.status)}
                    sx={{
                      backgroundColor: getStatusColor(selectedProject.status),
                      color: "white",
                      fontWeight: 600,
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    }}
                  />
                </Box>

                {/* Date and Time */}
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                    <ClockIcon sx={{ fontSize: { xs: "1rem", sm: "1.25rem" }, color: "primary.main" }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                      Schedule
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ ml: 3.5, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                    {dayjs(selectedProject.start_date).format("dddd, MMMM DD, YYYY")}
                  </Typography>
                  <Typography variant="body2" sx={{ ml: 3.5, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                    {dayjs(selectedProject.start_date).format("hh:mm A z")} - {dayjs(selectedProject.end_date).format("hh:mm A z")}
                  </Typography>
                </Box>

                {/* Location */}
                {selectedProject.location && (
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <LocationOnIcon sx={{ fontSize: { xs: "1rem", sm: "1.25rem" }, color: "primary.main" }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                        {locationLabel}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ ml: 3.5, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                      {selectedProject.location.name || "N/A"}
                    </Typography>
                    <Typography variant="body2" sx={{ ml: 3.5, fontSize: { xs: "0.8rem", sm: "0.875rem" }, color: "text.secondary" }}>
                      {selectedProject.location.address_1}, {selectedProject.location.city}, {selectedProject.location.state} {selectedProject.location.zip}
                    </Typography>
                  </Box>
                )}

                {/* Company */}
                {selectedProject.company && (
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <BusinessIcon sx={{ fontSize: { xs: "1rem", sm: "1.25rem" }, color: "primary.main" }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                        Company
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ ml: 3.5, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                      {selectedProject.company.name || "N/A"}
                    </Typography>
                  </Box>
                )}

                {/* Manager */}
                {selectedProject.manager && (
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <PersonIcon sx={{ fontSize: { xs: "1rem", sm: "1.25rem" }, color: "primary.main" }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                        Manager
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ ml: 3.5, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                      {selectedProject.manager.first_name} {selectedProject.manager.last_name}
                    </Typography>
                  </Box>
                )}

                {/* Description */}
                {selectedProject.description && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                      Description
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: { xs: "0.8rem", sm: "0.875rem" },
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {selectedProject.description}
                    </Typography>
                  </Box>
                )}

                {/* Additional Info */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                    Additional Information
                  </Typography>
                  <Stack spacing={1}>
                    {selectedProject.type && (
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, color: "text.secondary" }}>
                          Type:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, fontWeight: 500 }}>
                          {selectedProject.type === "hourly" ? "Hourly" : "Fixed"}
                        </Typography>
                      </Box>
                    )}
                    {selectedProject.hourly_rate && (
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, color: "text.secondary" }}>
                          Hourly Rate:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, fontWeight: 500 }}>
                          ${selectedProject.hourly_rate.toLocaleString()}/hr
                        </Typography>
                      </Box>
                    )}
                    {selectedProject.total_pay && (
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, color: "text.secondary" }}>
                          Total Pay:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, fontWeight: 500 }}>
                          ${selectedProject.total_pay.toLocaleString()}
                        </Typography>
                      </Box>
                    )}
                    {selectedProject.approximate_hours_to_complete && (
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, color: "text.secondary" }}>
                          Approximate Hours:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, fontWeight: 500 }}>
                          {selectedProject.approximate_hours_to_complete} hours
                        </Typography>
                      </Box>
                    )}
                    {selectedProject.num_of_talent && (
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, color: "text.secondary" }}>
                          Number of Talent:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, fontWeight: 500 }}>
                          {selectedProject.num_of_talent}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>
              </Stack>
            )}
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Button
              onClick={() => {
                setOpenModal(false);
                setSelectedProject(null);
              }}
              sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
            >
              Close
            </Button>
            {selectedProject && (
              <Button
                variant="contained"
                onClick={() => {
                  navigate(`/works/${selectedProject.id}`);
                  setOpenModal(false);
                  setSelectedProject(null);
                }}
                endIcon={<ArrowForwardIcon />}
                sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
              >
                Go To Detail
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default MonthlyCalendar;
/**
 * Header:
 * icon for switching to the previous month,
 * formatted date showing current month and year,
 * another icon for switching to next month
 * icons should also handle onClick events to change a month
 */
